import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import type { DatabaseSourceInfo } from '@ditto/shared';
import { useEffect, useState, type FC } from 'react';
import { createRegexRenderer, RichTextarea } from 'rich-textarea';

// Query syntax highlighting renderer (colors from Figma design)
const queryRenderer = createRegexRenderer([
  [/\bAND\b/g, { color: '#ec7839', fontWeight: 500 }], // Orange for AND
  [/\b(OR|NOT)\b/g, { color: '#e261e5', fontWeight: 500 }], // Pink/Magenta for OR, NOT
  [/"[^"]*"/g, { color: '#3a93ff' }], // Blue for quoted strings
  [/\b\d{4}\b/g, { color: '#25c329' }], // Green for years/numbers
  [/[()[\]]/g, { color: '#737373' }], // Gray for parentheses and brackets
]);

interface SimpleTextInputsProps {
  initialQuery: string;
  onQueryChange: (query: string, isValid: boolean) => void;
  source: DatabaseSourceInfo;
  showErrors: boolean;
  setShowErrors: (showErrors: boolean) => void;
}

export const SimpleTextInputs: FC<SimpleTextInputsProps> = ({
  initialQuery,
  onQueryChange,
  source,
  showErrors,
  setShowErrors,
}) => {
  const [query, setQuery] = useState(initialQuery);

  // Initialize from initialQuery
  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  // Notify parent of changes
  useEffect(() => {
    const isValid = query.trim().length > 0;
    if (isValid) {
      setShowErrors(false);
    }
    onQueryChange(query, isValid);
  }, [query, onQueryChange]);

  return (
    <div className="flex flex-col gap-1 w-full">
      <Text variant="body-sm" className="font-medium">
        <span className="text-red-500">*</span>Database query
      </Text>
      <RichTextarea
        placeholder={source.queryPlaceholder}
        value={query}
        onChange={e => setQuery(e.target.value)}
        className={cn(
          'rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          showErrors && 'border-red-500'
        )}
        style={{ width: '100%', minHeight: 100 }}
      >
        {queryRenderer}
      </RichTextarea>
      {showErrors && (
        <Text variant="body-xs" color="danger">
          A query is required to add database items as rows in the table
        </Text>
      )}
    </div>
  );
};
